<template>
  <!-- `table-wrapper` gives the table the theme's dark rows, as elsewhere. -->
  <div class="summary-rows table-wrapper">
    <el-table
      :data="rows"
      row-key="key"
      :span-method="spanChoice"
      :row-class-name="groupClass"
      @cell-mouse-enter="hoverGroup"
      @cell-mouse-leave="hoveredGroup = null"
    >
      <el-empty
        slot="empty"
        :description="$t(`${I18N}.empty`)"
        :image-size="80"
      />

      <el-table-column
        :label="$t(`${I18N}.columns.sample`)"
        min-width="170"
      >
        <template slot-scope="{ row }">
          <!-- The sample, as resolved; the row locates it in the file. -->
          <div>{{ row.row.labSampleCode }}</div>
          <div class="summary-rows__secondary">
            {{
              $t(`${I18N}.columns.sourceRow`, {
                row: row.row.sourceRow,
                column: row.row.column,
              })
            }}
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :label="$t(`${I18N}.columns.analysis`)"
        min-width="200"
      >
        <template slot-scope="{ row }">
          <div>{{ row.row.analysisCode || EMPTY }}</div>
          <div class="summary-rows__secondary">
            {{ row.row.analysisDescription }}
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :label="$t(`${I18N}.columns.value`)"
        width="130"
      >
        <el-input
          slot-scope="{ row }"
          :value="row.valueText"
          size="small"
          :disabled="!row.includable"
          :label="$t(`${I18N}.inputs.value`)"
          @input="$emit('value', { key: row.key, text: $event })"
        />
      </el-table-column>

      <el-table-column
        :label="$t(`${I18N}.columns.ld`)"
        width="110"
      >
        <el-input
          slot-scope="{ row }"
          :value="row.ldText"
          size="small"
          :disabled="!row.includable"
          :label="$t(`${I18N}.inputs.ld`)"
          @input="$emit('ld', { key: row.key, text: $event })"
        />
      </el-table-column>

      <el-table-column
        :label="$t(`${I18N}.columns.measure`)"
        min-width="140"
      >
        <MeasureSelect
          slot-scope="{ row }"
          :value="row.measureId"
          :measure="row.measure"
          :disabled="!row.includable"
          @change="$emit('measure', { key: row.key, ...$event })"
        />
      </el-table-column>

      <el-table-column
        :label="$t(`${I18N}.columns.proposed`)"
        min-width="110"
      >
        <template slot-scope="{ row }">
          <div>{{ row.written.value || EMPTY }}</div>
          <div
            v-if="row.written.LD"
            class="summary-rows__secondary"
          >
            {{ $t(`${I18N}.proposedLd`, { ld: row.written.LD }) }}
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :label="$t(`${I18N}.columns.current`)"
        min-width="130"
      >
        <template slot-scope="{ row }">
          <div>{{ row.row.currentValue || EMPTY }}</div>
          <div
            v-if="row.row.currentLD"
            class="summary-rows__secondary"
          >
            {{ $t(`${I18N}.proposedLd`, { ld: row.row.currentLD }) }}
          </div>
          <div
            v-if="row.overwrite"
            class="summary-rows__secondary"
          >
            <i
              class="el-icon-refresh-right"
              aria-hidden="true"
            />
            {{ $t(`${I18N}.overwrite`) }}
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :label="$t(`${I18N}.columns.status`)"
        min-width="220"
      >
        <template slot-scope="{ row }">
          <div class="summary-rows__tags">
            <el-tag
              :type="stateOf(row).type"
              size="mini"
            >
              <i
                :class="stateOf(row).icon"
                aria-hidden="true"
              />
              {{ stateOf(row).title }}
            </el-tag>
            <!-- Beside the state only where it does not say the opposite. -->
            <el-tag
              v-if="isNonCompliantBeside(row)"
              type="warning"
              size="mini"
            >
              <i
                class="el-icon-warning"
                aria-hidden="true"
              />
              {{ $t(`${I18N}.nonCompliant`) }}
            </el-tag>
          </div>
          <div
            v-if="stateOf(row).fix"
            class="summary-rows__secondary"
          >
            {{ stateOf(row).fix }}
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :label="$t(`${I18N}.columns.include`)"
        :column-key="CHOICE_COLUMN"
        min-width="220"
      >
        <template slot-scope="{ row }">
          <!-- One choice for the candidates of an ambiguous cell (C28, C58).
               `input`, not `change`: el-radio-group emits `change` with its
               `value`, which only `input` updates. -->
          <el-radio-group
            v-if="row.group"
            :value="row.group.choice"
            :aria-label="$t(`${I18N}.chooseOne`)"
            class="summary-rows__choice"
            @input="$emit('choose', { keys: row.group.keys, key: $event })"
          >
            <el-radio
              v-for="option in row.group.options"
              :key="option.key"
              :label="option.key"
              :disabled="option.disabled"
            >
              {{ option.code }}
            </el-radio>
            <el-radio :label="NOT_IMPORTED">
              {{ $t(`${I18N}.notImported`) }}
            </el-radio>
          </el-radio-group>
          <el-checkbox
            v-else
            :value="row.included"
            :disabled="!row.includable"
            :aria-label="$t(`${I18N}.inputs.include`)"
            @change="$emit('include', { key: row.key, included: $event })"
          />
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
  import MeasureSelect from './MeasureSelect.vue';
  import { ROW_STATES } from './summaryRows';

  // The value of "Non importare" in the choice of an ambiguous group.
  export const NOT_IMPORTED = 'none';

  const EMPTY = '—';
  const CHOICE_COLUMN = 'include';
  const GROUP_HOVER_CLASS = 'summary-rows__group--hover';

  const STATE_TAGS = Object.freeze({
    [ROW_STATES.OK]: { type: 'success', icon: 'el-icon-circle-check' },
    [ROW_STATES.EXCLUDED]: { type: 'info', icon: 'el-icon-remove-outline' },
    [ROW_STATES.ERROR]: { type: 'danger', icon: 'el-icon-warning-outline' },
  });

  // A valid row out of specification: written all the same (REQ-17).
  const NON_COMPLIANT_TAG = Object.freeze({
    type: 'warning',
    icon: 'el-icon-warning',
  });

  const isNonCompliant = (row) => row.includable && row.compliant === false;

  // The rows of the summary step (FE-5), as given: it emits every edit and
  // keeps no state of its own. The choice of an ambiguous group spans the
  // rows of its candidates, which the step keeps next to each other.
  export default {
    name: 'SummaryRowsTable',
    components: { MeasureSelect },
    props: {
      rows: { type: Array, required: true },
    },
    data() {
      return {
        I18N: 'analysesData.importValues.summary',
        NOT_IMPORTED,
        CHOICE_COLUMN,
        EMPTY,
        // First key of the ambiguous group under the pointer.
        hoveredGroup: null,
      };
    },
    methods: {
      stateOf(view) {
        const { state, row } = view;
        const tag = STATE_TAGS[state.state];
        // "Valida" next to "Valore non conforme" reads as a contradiction.
        if (state.state === ROW_STATES.OK && isNonCompliant(view)) {
          return {
            ...NON_COMPLIANT_TAG,
            title: this.$t(`${this.I18N}.nonCompliant`),
          };
        }
        if (state.state !== ROW_STATES.ERROR) {
          return {
            ...tag,
            title: this.$t(`${this.I18N}.states.${state.state}`),
          };
        }
        const key = `${this.I18N}.errors.${state.errorCode}`;
        if (!this.$te(`${key}.fix`)) {
          return {
            ...tag,
            title: this.$t(`${this.I18N}.errors.unknown.title`),
            fix: row.errorMessage,
          };
        }
        return {
          ...tag,
          title: this.$t(`${key}.title`),
          fix: this.$t(`${key}.fix`),
        };
      },
      isNonCompliantBeside(view) {
        return view.state.state !== ROW_STATES.OK && isNonCompliant(view);
      },
      // Element hovers a row, while the choice spans the whole group: the
      // candidates light up together, so the choice never looks like a part of
      // the first row lying over the others.
      hoverGroup(row) {
        this.hoveredGroup = row.group ? row.group.keys[0] : null;
      },
      groupClass({ row }) {
        return row.group && row.group.keys[0] === this.hoveredGroup
          ? GROUP_HOVER_CLASS
          : '';
      },
      spanChoice({ row, column }) {
        if (!row.group || column.columnKey !== CHOICE_COLUMN) return [1, 1];
        return row.group.first ? [row.group.size, 1] : [0, 0];
      },
    },
  };
</script>

<style lang="scss" scoped>
  .summary-rows__secondary {
    color: $--color-text-regular;
    font-size: 0.85em;
    line-height: 1.4;
  }

  .summary-rows__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  .summary-rows ::v-deep .summary-rows__group--hover > td {
    background-color: $--table-row-hover-background-color;
  }

  // One candidate per line, the codes being long. A flex column with a gap:
  // no trailing margin and no inline line box, so the cell's middle alignment
  // centres the choice across the rows it spans.
  .summary-rows__choice {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;

    .el-radio {
      margin-right: 0;
    }
  }
</style>
